/**
 * The rules every app-wide keyboard listener shares (UndoProvider's Ctrl+Z, the command registry's shortcuts — #900),
 * written once so they can't drift.
 */

/**
 * The keystroke is the user TYPING: an editable field has it (an input that takes text, a textarea, a select, or
 * anything contenteditable — CodeMirror, ProseMirror). A global shortcut stands aside for these: their own native
 * behaviour (Backspace deletes a character, Ctrl+C copies the text selection, Ctrl+Z undoes typing) is what the user
 * means. Swallowing Backspace in a textarea would be far worse than a shortcut that does nothing.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true
  if (target instanceof HTMLInputElement) {
    // Buttons, checkboxes and radios take no text — a shortcut over them is not typing.
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image'].includes(target.type)
  }
  return false
}

/**
 * Windows reports AltGr as Ctrl+Alt (and a physical Ctrl+Alt IS AltGr there), so on Polish, Croatian and Hungarian
 * layouts a chord meant to type a character reads as Ctrl+<letter> (#829). No shortcut here uses Ctrl+Alt, so such a
 * chord is never a shortcut.
 */
export function isAltGrChord(e: KeyboardEvent): boolean {
  return e.altKey && e.ctrlKey
}
